/**
 * Global tracker utility functions
 * Provides helper functions for accessing the global HumanBehavior tracker instance
 */

/**
 * Identifies a user using the global HumanBehavior tracker
 * @param userProperties - User properties to identify with
 * @param identityToken - Optional token minted by your backend attesting this user
 * @returns Promise<string> - The endUserId if successful, null if tracker not found
 */
export function identifyUserGlobally(
  userProperties: Record<string, any>,
  identityToken?: string
): Promise<string> | null {
  const globalTracker = (globalThis as any).__humanBehaviorGlobalTracker;
  
  if (globalTracker?.identifyUser) {
    return globalTracker.identifyUser({ userProperties, identityToken });
  } else {
    console.warn('HumanBehavior tracker not found. Make sure the SDK is initialized.');
    return null;
  }
}

/**
 * Sends an event using the global HumanBehavior tracker.
 *
 * Resolves the singleton from window (where the tracker constructor stashes
 * `this`) with a globalThis fallback so this still works in non-DOM
 * environments. Calls `customEvent`, which is the SDK's public API for
 * named events. (Pre-0.7 there was an alias `track`; it was removed when
 * we unified on `customEvent`.)
 */
export function sendEventGlobally(eventName: string, properties?: Record<string, any>): Promise<void> | null {
  const root: any = typeof window !== 'undefined' ? window : globalThis;
  const globalTracker = root.__humanBehaviorGlobalTracker;

  if (globalTracker?.customEvent) {
    return globalTracker.customEvent(eventName, properties);
  }
  // Back-compat: if a future build re-introduces `track`, prefer it.
  if (globalTracker?.track) {
    return globalTracker.track(eventName, properties);
  }
  console.warn('HumanBehavior tracker not found. Make sure the SDK is initialized.');
  return null;
}

/**
 * Checks if the global HumanBehavior tracker is available
 * @returns boolean - True if tracker is available
 */
export function isGlobalTrackerAvailable(): boolean {
  const globalTracker = (globalThis as any).__humanBehaviorGlobalTracker;
  return !!(globalTracker?.identifyUser);
}
